import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    ChartConfiguration,
    Legend,
    LinearScale,
    Tooltip
} from 'chart.js';
import { WarehouseChargedSummary, WarehouseInvoiceService } from 'src/app/services/warehouse-invoice.service';
import { Warehouse, WarehouseService } from 'src/app/services/warehouse.service';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface SupplyBreakdown {
    /** Nombre del insumo, tal cual aparece en el desglose. */
    name: string;
    /** Color del punto en "Top insumos por gasto". */
    color: string;
    charged: number;
    spent: number;
}

type WarehouseTab = 'kpis' | 'invoices' | 'receivable';

const GREEN = '#2EBD6B';
const RED = '#EF4B5C';

/**
 * Catálogo del desglose por insumo: color del punto en "Top insumos por gasto"
 * y proporción de gasto sobre lo cobrado.
 *
 * Lo cobrado sale de las facturas. El gasto no existe en ningún lado del
 * sistema, así que lo que sigue siendo de relleno es la *proporción*, no el
 * importe: con un importe fijo, un insumo con pocas facturas mostraría margen
 * negativo en cuanto lo cobrado bajara del gasto. Las proporciones son las que
 * tenían los importes de relleno originales, así que los márgenes por insumo
 * son los mismos de antes.
 */
const SUPPLIES: Array<{ name: string; color: string; spentRatio: number }> = [
    { name: 'Preparación paquetería', color: '#2F80ED', spentRatio: 0.6657 },
    { name: 'Preparación paquetería palets', color: '#27AE60', spentRatio: 0.6209 },
    { name: 'Preparación de pedidos fuera de horario (cargas, descargas, maniobras)', color: '#F2C94C', spentRatio: 0.7735 },
    { name: 'Insumos (fleje, carton, esquineros...)', color: '#EB5757', spentRatio: 0.7107 },
    { name: 'Tarimas para salidas', color: '#9B51E0', spentRatio: 0.6443 },
    { name: 'Playos', color: '#2D9CDB', spentRatio: 0.6530 },
    { name: 'Papeleria', color: '#F2994A', spentRatio: 0.6530 },
    { name: 'Paquetería guias', color: '#EB5FA6', spentRatio: 0.6530 }
];

/** Para un insumo dado de alta desde la pantalla de facturación, que no está en
 *  el catálogo de arriba. La proporción es la media del catálogo. */
const DEFAULT_SUPPLY = { color: '#8A8A8A', spentRatio: 0.6709 };

@Component({
    selector: 'app-invoicing-warehouse',
    templateUrl: './invoicing-warehouse.component.html',
    styleUrls: ['./invoicing-warehouse.component.css']
})
export class InvoicingWarehouseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('incomeChart') incomeChartRef?: ElementRef<HTMLCanvasElement>;

    activeTab: WarehouseTab = 'kpis';
    /** BehaviorSubject: hay almacén desde el primer render, nunca es nulo. */
    currentWarehouse: Warehouse = this.warehouseService.current;

    /** Mes del desglose. Formato `aaaa-mm`, el que espera <input type="month">. */
    selectedMonth = '2026-07';

    // ---------------------------------------------------------------------
    // Todo lo cobrado —tarjetas, desglose y gráfico— se calcula desde las
    // facturas de almacén, no se escribe a mano: es la única forma de que las
    // tres vistas y la pestaña de listado no se contradigan.
    //
    // Lo que sigue sin origen de datos es el gasto (el sistema no registra
    // costes de almacén) y los deltas semanales de las tarjetas.
    // ---------------------------------------------------------------------

    kpis = {
        charged: 0,
        spent: 0,
        profit: 0,
        margin: 0,
        // De relleno: no hay serie histórica contra la que comparar.
        chargedDelta: 12.4,
        spentDelta: -5.2,
        profitDelta: 21.7,
        marginDelta: 4.8
    };

    breakdown: SupplyBreakdown[] = [];

    private monthlySeries: { labels: string[]; charged: number[]; spent: number[] } = {
        labels: [],
        charged: [],
        spent: []
    };

    private chart: Chart | null = null;
    private warehouseSub?: Subscription;
    private summarySub?: Subscription;

    constructor(
        private warehouseService: WarehouseService,
        private warehouseInvoiceService: WarehouseInvoiceService,
        private zone: NgZone
    ) {}

    ngOnInit(): void {
        this.warehouseSub = this.warehouseService.current$.subscribe(warehouse => {
            this.currentWarehouse = warehouse;
            this.loadSummary();
        });
    }

    ngAfterViewInit(): void {
        this.buildChart();
    }

    ngOnDestroy(): void {
        this.warehouseSub?.unsubscribe();
        this.summarySub?.unsubscribe();
        this.chart?.destroy();
    }

    private loadSummary(): void {
        // Al cambiar de almacén se descarta la petición anterior: si llegara
        // después, pintaría los KPI's del almacén que ya no está seleccionado.
        this.summarySub?.unsubscribe();
        this.summarySub = this.warehouseInvoiceService
            .chargedSummary(this.currentWarehouse.name)
            .subscribe(summary => {
                this.applySummary(summary);
                if (this.activeTab === 'kpis') this.buildChart();
            });
    }

    /**
     * Vuelca el resumen de facturación en las tres vistas de la pestaña.
     *
     * El total de las tarjetas se toma de la suma del desglose, no del total
     * del resumen, para que la tarjeta no pueda mostrar una cifra distinta de
     * la que suma la tabla que tiene debajo.
     */
    private applySummary(summary: WarehouseChargedSummary): void {
        // Las filas salen del resumen y no del catálogo fijo: así un insumo
        // dado de alta al facturar aparece en el desglose, y su importe no se
        // queda fuera de la tarjeta mientras sí cuenta en el listado.
        const catalog = new Map(SUPPLIES.map(supply => [supply.name, supply]));

        this.breakdown = summary.byConcept.map(row => {
            const supply = catalog.get(row.concept) ?? DEFAULT_SUPPLY;
            return {
                name: row.concept,
                color: supply.color,
                charged: row.charged,
                spent: this.round2(row.charged * supply.spentRatio)
            };
        });

        const charged = this.round2(this.breakdown.reduce((acc, row) => acc + row.charged, 0));
        const spent = this.round2(this.breakdown.reduce((acc, row) => acc + row.spent, 0));

        this.kpis = {
            ...this.kpis,
            charged,
            spent,
            profit: this.round2(charged - spent),
            margin: charged ? ((charged - spent) / charged) * 100 : 0
        };

        // El gasto del mes se reparte con la proporción de cada insumo, así que
        // las barras suman exactamente el gasto total de las tarjetas.
        const ratioOf = (concept: string) =>
            catalog.get(concept)?.spentRatio ?? DEFAULT_SUPPLY.spentRatio;
        this.monthlySeries = {
            labels: summary.byMonth.map(month => month.label),
            charged: summary.byMonth.map(month => month.charged),
            spent: summary.byMonth.map(month => this.round2(month.byConcept.reduce(
                (acc, cell) => acc + cell.charged * ratioOf(cell.concept), 0)))
        };

        this.computeTopSupplies();
    }

    private round2(value: number): number {
        return Math.round(value * 100) / 100;
    }

    setTab(tab: WarehouseTab): void {
        if (this.activeTab === tab) return;
        this.activeTab = tab;
        // El canvas sólo existe mientras la pestaña de KPI's está montada, así
        // que el gráfico se reconstruye al volver a ella.
        if (tab === 'kpis') {
            setTimeout(() => this.buildChart());
        } else {
            this.chart?.destroy();
            this.chart = null;
        }
    }

    /**
     * "Top insumos por gasto": se deriva del mismo desglose de abajo para que
     * ambos paneles no puedan contradecirse, ordenado de mayor a menor gasto.
     *
     * Se calcula al cambiar el desglose y no en un getter: desde el template
     * un getter se evalúa en cada ciclo de detección de cambios y devolvería
     * objetos nuevos, obligando a *ngFor a rehacer la lista entera cada vez.
     */
    topSupplies: Array<SupplyBreakdown & { share: number }> = [];

    private computeTopSupplies(): void {
        const totalSpent = this.breakdown.reduce((acc, row) => acc + row.spent, 0) || 1;
        this.topSupplies = [...this.breakdown]
            .sort((a, b) => b.spent - a.spent)
            .map(row => ({ ...row, share: (row.spent / totalSpent) * 100 }));
    }

    profitOf(row: SupplyBreakdown): number {
        return row.charged - row.spent;
    }

    marginOf(row: SupplyBreakdown): number {
        if (!row.charged) return 0;
        return (this.profitOf(row) / row.charged) * 100;
    }

    /** Etiqueta larga del mes seleccionado, p. ej. "Julio". */
    get monthLabel(): string {
        const [year, month] = this.selectedMonth.split('-').map(Number);
        if (!year || !month) return '';
        const label = new Date(year, month - 1, 1).toLocaleDateString('es-MX', { month: 'long' });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
    }

    formatPercent(value: number): string {
        return `${(value || 0).toFixed(1)}%`;
    }

    /** `+12.4%` / `-5.2%`: el signo se muestra siempre. */
    formatDelta(value: number): string {
        const sign = value > 0 ? '+' : '';
        return `${sign}${(value || 0).toFixed(1)}%`;
    }

    downloadPdf(): void {
        // Pendiente del endpoint de reportes de almacén.
    }

    private buildChart(): void {
        const canvas = this.incomeChartRef?.nativeElement;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        this.chart?.destroy();

        const config: ChartConfiguration<'bar'> = {
            type: 'bar',
            data: {
                labels: this.monthlySeries.labels,
                datasets: [
                    {
                        label: 'Cobrado',
                        data: this.monthlySeries.charged,
                        backgroundColor: GREEN,
                        borderRadius: 3,
                        barPercentage: 0.72,
                        categoryPercentage: 0.62
                    },
                    {
                        label: 'Gastado',
                        data: this.monthlySeries.spent,
                        backgroundColor: RED,
                        borderRadius: 3,
                        barPercentage: 0.72,
                        categoryPercentage: 0.62
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    // La leyenda se pinta en el HTML para poder alinearla con
                    // el título del panel, como en el diseño.
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (item) => ` ${item.dataset.label}: ${this.formatCurrency(item.raw as number)}`
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: { color: '#8A8A8A', font: { size: 12 } }
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: '#EFEFEF' },
                        border: { display: false },
                        ticks: {
                            color: '#8A8A8A',
                            font: { size: 12 },
                            callback: (value) => `$${Number(value) / 1000}K`
                        }
                    }
                }
            }
        };

        // Fuera de la zona: chart.js observa el tamaño del contenedor y cada
        // notificación dispararía detección de cambios, que a su vez redibuja.
        this.zone.runOutsideAngular(() => {
            this.chart = new Chart(ctx, config);
        });
    }
}
